import { useCallback, useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { Button } from '@pwc/appkit-react/lib/Button';
import { Routes } from '@common-packages/routes-definitions';
import cloneDeep from 'lodash.clonedeep';
import config from '../../config';

import { useRowEditMode } from '../../shared/editMode';
import AgGrid from '../../shared/displayComponents/agGrid/agGrid.component';
import useModal from '../../shared/hooks/useModal.hook';
import { showConfirmModal } from '../../shared/confirmModal/store/actions';
import useUrlParams from '../../sharedSubPages/returnWorkspace/hooks/useUrlParams.hook';
import { reduceQueryString } from '../../utils/routerUtils';
import {
  customerDataSelector,
  isEngagementAdminRoleSelector,
} from '../../shared/store/customerData.selectors';
import useFetch from '../../shared/hooks/useFetch.hook';
import { errorNotification } from '../../shared/notification/store/actions';

import { NOT_ALLOW_EDIT_OWN_ACCOUNT_MESSAGE, isOwnInfo } from './utils';
import { ONE_YEAR_IN_DAYS, ONE_DAY_IN_MILLISECONDS } from '../../shared/constants';
import { resetValidationResult, deleteUser, fetchUsers } from './store/actions';
import AddUserFormModal from './addUserFormModal.container';
import {
  usersSelector,
  isFetchingUsersSelector,
  isUpdatingUserSelector,
  isCreatingUserSelector,
  isDeletingUserSelector,
} from './store/selectors';
import getColumnDefinitions from './usersMaintenance.columnDefinitions';
import * as api from './store/api';
import styles from './usersMaintenance.module.scss';

const UsersMaintenance = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const customerData = useSelector(customerDataSelector);
  const users = useSelector(usersSelector);
  const isEngagementAdminRole = useSelector(isEngagementAdminRoleSelector);
  const isFetchingUsers = useSelector(isFetchingUsersSelector);
  const isUpdatingUser = useSelector(isUpdatingUserSelector);
  const isCreatingUser = useSelector(isCreatingUserSelector);
  const isDeletingUser = useSelector(isDeletingUserSelector);
  const { isFetching: isUpdatingUsers, fetch: updateUsers } = useFetch({
    action: api.updateUsers,
    successNotificationMessage: 'Users updated successfully',
  });

  const { showModal, modalProps } = useModal();
  const [userToEdit, setUserToEdit] = useState(null);

  const { queryParams } = useUrlParams();
  const { userId: userIdFromUrl } = queryParams;
  const [selectedUserId, setSelectedUserId] = useState(userIdFromUrl ?? null);

  useEffect(() => {
    setSelectedUserId(userIdFromUrl ?? null);
  }, [userIdFromUrl]);

  const expireDateEditorParams = useMemo(() => {
    const minDate = new Date();
    minDate.setHours(0, 0, 0, 0);
    const maxDate = new Date(minDate.getTime() + ONE_YEAR_IN_DAYS * ONE_DAY_IN_MILLISECONDS);

    return { minDate, maxDate };
  }, []);

  const buttonsDisabled = isFetchingUsers || isUpdatingUser || isUpdatingUsers;

  const onSave = useCallback(
    async ({ rowsPairsWithChanges }) => {
      try {
        if (rowsPairsWithChanges.length > 0) {
          const rowsToUpdate = rowsPairsWithChanges.map(row => row.newRow);
          await updateUsers({ updatedUsers: rowsToUpdate });
        }
        dispatch(fetchUsers());
      } catch (error) {
      } finally {
      }
    },
    [dispatch, updateUsers],
  );

  const redirectToAssignBusinesses = useCallback(
    () =>
      history.push({
        pathname: Routes.assignBusinesses.MAIN,
        search: reduceQueryString({ userId: selectedUserId }),
      }),
    [history, selectedUserId],
  );

  const onCancel = useCallback(() => {}, []);

  const openModalForAdd = useCallback(() => {
    dispatch(resetValidationResult());
    setUserToEdit(null);
    showModal();
  }, [dispatch, showModal]);

  const openModalForEdit = useCallback(
    row => {
      dispatch(resetValidationResult());
      setUserToEdit(row);
      showModal();
    },
    [dispatch, showModal],
  );

  const onDeleteUser = useCallback(
    ({ userId: deletedUserId }) =>
      dispatch(
        showConfirmModal({
          title: `Delete user's access?`,
          text: `Are you sure you want to remove this user's access for this client?`,
          confirmCallback: async () => {
            await dispatch(deleteUser(deletedUserId));
            dispatch(fetchUsers());
          },
        }),
      ),
    [dispatch],
  );

  const hasExpirationEmpty = dataRows => {
    return dataRows.length > 0 && dataRows.some(item => !item?.expireDate);
  };

  const getUniqueRowId = ({ data: { userId: rowUserId } }) => rowUserId;
  const isLoading = isFetchingUsers || isUpdatingUser || isCreatingUser || isDeletingUser;
  const {
    navigationPrompt,
    isInEditMode,
    editModeButtons,
    clonedRowData,
    updateRow,
    onGridReady,
    gridApi,
  } = useRowEditMode({
    onSave,
    onCancel,
    rowData: users,
    getUniqueRowId,
    editButtonDisabled: buttonsDisabled,
    appendRowUnderSelectedRow: true,
    saveButtonDisabled: buttonsDisabled || hasExpirationEmpty,
    isFetchAllRows: true,
  });

  const onResetFiltersClick = useCallback(() => {
    gridApi?.setFilterModel(null);
  }, [gridApi]);

  const handleGridReady = useCallback(
    params => {
      onGridReady(params);

      if (userIdFromUrl) {
        params.api.forEachNode(node => {
          if (node.data?.userId === userIdFromUrl) {
            node.setSelected(true, true);
          }
        });
      }
    },
    [onGridReady, userIdFromUrl],
  );

  const handleSelectionChanged = useCallback(({ api }) => {
    if (api.getEditingCells?.()?.length > 0) {
      return;
    }

    const selectedRow = api.getSelectedRows()[0];
    setSelectedUserId(selectedRow?.userId ?? null);
  }, []);

  const handleCellClicked = useCallback(({ node, column, api }) => {
    if (!node || column?.getColDef()?.field === 'expireDate') {
      return;
    }

    if (api.getEditingCells?.()?.length > 0) {
      return;
    }

    requestAnimationFrame(() => {
      node.setSelected(true, true);
    });
  }, []);

  const fillCells = useCallback(({ initialValues }) => initialValues[0], []);

  const handleEditIconClick = useCallback(
    row => {
      if (!config.isEnabledEditOwnAccount && isOwnInfo({ rowData: row, customerData })) {
        dispatch(errorNotification(NOT_ALLOW_EDIT_OWN_ACCOUNT_MESSAGE));
        return;
      }

      openModalForEdit(row);
    },
    [customerData, dispatch, openModalForEdit],
  );

  const columnDefinitions = useMemo(
    () =>
      getColumnDefinitions({
        customerData,
        updateRow,
        isInEditMode,
        expireDateEditorParams,
        onEditIconClick: handleEditIconClick,
        ...(isEngagementAdminRole ? {} : { onDeleteIconClick: onDeleteUser }),
      }),
    [
      customerData,
      handleEditIconClick,
      onDeleteUser,
      updateRow,
      isInEditMode,
      isEngagementAdminRole,
      expireDateEditorParams,
    ],
  );

  return (
    <>
      {navigationPrompt}
      <div className="row">
        <div className="col add-button-column">
          <Button
            size="lg"
            className="add-button"
            onClick={openModalForAdd}
            disabled={isFetchingUsers}
          >
            Add User
          </Button>
          <Button
            size="lg"
            className="add-button"
            onClick={redirectToAssignBusinesses}
            disabled={isFetchingUsers || !selectedUserId}
          >
            Assign Businesses
          </Button>
          <div className="col add-button-column">
            <div className="add-button-column">{editModeButtons}</div>
          </div>
        </div>
      </div>
      <div className={`row grid-row ${styles.dataGridContainer}`}>
        <div className="col">
          <div className={styles.datagridHeader}>
            <Link to={{}} onClick={onResetFiltersClick}>
              Reset Filters
            </Link>
          </div>
          <AgGrid
            rowData={clonedRowData}
            isGridLoading={isLoading && !isInEditMode}
            onGridReady={handleGridReady}
            columnDefs={columnDefinitions}
            withSearchBar
            suppressCellFocus
            suppressSizeColumnsToFit={isInEditMode}
            suppressResizeGridOnRowDataUpdated={isInEditMode}
            animateRows={false}
            getRowId={getUniqueRowId}
            enableRangeSelection={isInEditMode}
            enableFillHandle={isInEditMode}
            suppressMultiRangeSelection
            fillHandleDirection="y"
            fillOperation={fillCells}
            rowSelection={{
              mode: 'singleRow',
              checkboxes: false,
              headerCheckbox: false,
              enableClickSelection: false,
            }}
            onCellClicked={handleCellClicked}
            onSelectionChanged={handleSelectionChanged}
          />
        </div>
        <AddUserFormModal {...modalProps} userToEdit={userToEdit} />
      </div>
    </>
  );
};

export default UsersMaintenance;
